<template>
  <div id="general-controls">
    <div class="new-truck-button" v-if="showPallet && user.type != 6">
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn large flat icon color="black" v-on="on" @click="openNewTruckPage">
            <v-icon large>local_shipping</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("general_controls.new_truck") }}</span>
      </v-tooltip>
    </div>
    <div class="new-pallets" :class="{ 'new-pallets-right': !showPallet}" v-if="user.type != 6">
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            class="pallets-button"
            large
            flat
            icon
            color="black"
            v-on="on"
            @click="createNewPallet"
          >
            <v-icon large>add_box</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("pallet_list_controls.add_pallet") }}</span>
      </v-tooltip>
    </div>
    <div
      class="new-fictive-pallet"
      :class="{ 'new-fictive-pallet-right': !showPallet}"
      v-if="user.type != 6"
    >
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            class="pallets-button"
            large
            flat
            icon
            color="black"
            v-on="on"
            @click="createNewFictivePallet"
          >
            <v-icon large>add_circle</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("pallet_list_controls.add_fictive_pallet") }}</span>
      </v-tooltip>
    </div>
    <div class="print-pdf" :class="{'print-pdf-right': !showPallet, 'read-only': user.type == 6}">
      <v-menu offset-y>
        <template v-slot:activator="{ on }">
          <v-btn class="pallets-button" large flat icon color="black" v-on="on">
            <v-icon large>print</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-tile @click="exportPdf('palletsPreparationExport')">
            <v-list-tile-title>{{ $t("general_controls.pallets_prepare_pdf") }}</v-list-tile-title>
          </v-list-tile>
          <v-list-tile v-if="user.type < 4" @click="exportPdf('barcodesExport')">
            <v-list-tile-title>{{ $t("general_controls.barcodes_pdf") }}</v-list-tile-title>
          </v-list-tile>
          <v-list-tile @click="exportPdf('loadingExport')" v-if="confirmedCount() > 0">
            <v-list-tile-title>{{ $t("general_controls.loading_pdf") }}</v-list-tile-title>
          </v-list-tile>
          <v-list-tile
            @click="exportPdf('opticutFileExport')"
            v-if="confirmedCount() > 0 && user.type < 4"
          >
            <v-list-tile-title>{{ $t("general_controls.opticut_file") }}</v-list-tile-title>
          </v-list-tile>
        </v-list>
      </v-menu>
    </div>
    <div class="edit-pallets" :class="{ 'edit-pallets-right': !showPallet}" v-if="user.type != 6">
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            class="pallets-button"
            large
            flat
            icon
            color="black"
            v-on="on"
            @click="editPallets"
          >
            <v-icon large>settings</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("pallet_list_controls.edit_pallets") }}</span>
      </v-tooltip>
    </div>
    <div class="move-products" :class="{'move-products-right': !showPallet}" v-if="user.type != 6">
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            class="pallets-button"
            large
            flat
            icon
            color="black"
            v-on="on"
            @click="moveProducts"
          >
            <v-icon large>swap_horiz</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("general_controls.move_products") }}</span>
      </v-tooltip>
    </div>
    <div
      class="dont-make-pallets"
      :class="{'dont-make-pallets-right': !showPallet}"
      v-if="user.type != 6"
    >
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            class="pallets-button"
            large
            flat
            icon
            color="black"
            v-on="on"
            @click="dontMakePalletsPrompt"
          >
            <v-icon large>broken_image</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("general_controls.dont_print") }}</span>
      </v-tooltip>
    </div>
    <ConfirmBox ref="confirmBox" @confirmDialog="dialogConfirmed" />
  </div>
</template>

<script>
import ConfirmBox from "@/components/ConfirmBox.vue";
import Pallet from "@/models/Pallet.js";
import Export from "@/models/Export.js";
import User from "@/models/User.js";
export default {
  name: "general-controls",
  props: ["pallets", "trucks", "client", "selectedPallets", "showPallet"],
  data() {
    return {
      user: new User(this.$session.get("user")),
      confirmed: false,
    };
  },
  watch: {
    pallets() {
      this.confirmed = false;
    },
  },
  methods: {
    openNewTruckPage() {
      this.$emit("openNewTruck");
    },
    createNewPallet() {
      this.$emit("createNewPallet");
    },
    createNewFictivePallet() {
      this.$emit("createNewFictivePallet");
    },
    editPallets() {
      this.$emit("editPallets");
    },
    moveProducts() {
      this.$emit("moveProducts");
    },
    confirmedCount() {
      return Pallet.confirmed(this.pallets);
    },
    dontMakePalletsPrompt() {
      const method = { pallet_action: true };
      this.$refs.confirmBox.openDialog(
        this.$t("general_controls.confirm_dont_make"),
        method
      );
    },
    dontMakePallets() {
      this.$store.commit("setLoading", true);
      this.selectedPallets.forEach((pallet) => {
        pallet.printable = false;
      });
      const pallets =
        this.selectedPallets.length == 0 ? this.pallets : this.selectedPallets;
      Pallet.updatePallets(pallets, true);
      this.$emit("uncheck");
      this.$emit("updated");
    },
    async exportPdf(pdf) {
      const pallets =
        this.selectedPallets.length == 0 ? this.pallets : this.selectedPallets;
      const myPdf = Object.create({ action: pdf });
      if (!Pallet.allConfirmed(pallets)) {
        if (this.user.type == 6) {
          alert(this.$t("pallets_crud.chosen_pallets_not_prepared"));
          return;
        }
        if (pdf == "palletsPreparationExport" && !this.confirmed) {
          this.$refs.confirmBox.openDialog(
            this.$t("truck_pallets_export.confirm_download"),
            myPdf
          );
          this.confirmed = true;
          return;
        }
      }
      const pdfHelper = new Export(pallets, this.trucks, this.client);
      this.$store.commit("setLoading", true);
      await pdfHelper[myPdf.action](this);
      this.$store.commit("setLoading", false);
      this.$emit("uncheck");
      this.$emit("updated");
    },
    dialogConfirmed(method) {
      if (method.pallet_action) {
        this.dontMakePallets();
        return;
      }
      this.exportPdf(method.action);
    },
  },
  components: {
    ConfirmBox,
  },
};
</script>

<style scoped>
.new-truck-button {
  position: absolute;
  right: 0;
  top: 0;
}
.new-pallets {
  position: absolute;
  right: 100px;
  top: 0;
}
.new-pallets-right {
  right: 50px;
}
.print-pdf {
  position: absolute;
  right: 150px;
  top: 0;
}
.print-pdf-right {
  right: 100px;
}
.edit-pallets {
  position: absolute;
  right: 200px;
  top: 0;
}
.edit-pallets-right {
  right: 150px;
}
.move-products {
  position: absolute;
  right: 250px;
  top: 0;
}
.move-products-right {
  right: 200px;
}
.dont-make-pallets {
  position: absolute;
  right: 300px;
  top: 0;
}
.dont-make-pallets-right {
  right: 250px;
}
.new-fictive-pallet {
  position: absolute;
  top: 0px;
  right: 50px;
}
.new-fictive-pallet-right {
  position: absolute;
  top: 0;
  right: 0;
}
.read-only {
  right: 0;
}
</style>
